import {
  addVisualAssessment,
  deleteVisualAssessment,
  updateVisualAssessment,
  WorkOrder,
} from "@/services/backend/htz/work-order/types/work-order";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import { useEffect, useState } from "react";
import { ScrollArea } from "@/shared/components/ui/scroll-area";
import { useInspectionContext } from "@/routes/processes/[processId]/htz/work-orders/[workOrderId]/positions/_components/inspection-context";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import t from "@/lang/lang";
import { cn } from "@/shared/lib/utils";
import {
  fromCatalogItem,
  VisualAssessment,
} from "@/services/backend/htz/inspection/types/visual-assessment";
import { Button } from "@/shared/components/ui/button";
import { AlertCircle, Plus, PlusCircle, Trash2 } from "lucide-react";
import { H4 } from "@/shared/components/typography";
import { Separator } from "@/shared/components/ui/separator";
import { Label } from "@/shared/components/ui/label";
import { InputValidationErrors } from "@/shared/components/errors/input-error-messages";
import { Textarea } from "@/shared/components/ui/textarea";
import { v4 } from "uuid";
import {
  Item,
  itemsForComponentKind,
} from "@/services/backend/htz/inspection/types/visual-assessment-catalog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import { useToast } from "@/shared/hooks/use-toast";
import { parseRTKQueryError } from "@/shared/components/errors/parse-r-t-k-query-error";
import { hasFieldError } from "@/shared/lib/errors/validation-error";
import { useGetVisualAssessmentCatalog } from "@/routes/processes/[processId]/htz/work-orders/[workOrderId]/_components/use-visual-assessment-catalog-offline";
import { useErrDocumentNotReady } from "@/shared/bsm/core/document/components/ui/err-document-not-ready";
import {
  ResultDot,
  ResultSelect,
} from "@/services/backend/htz/inspection/components/inspection-result";
import { TableCard } from "@/shared/components/tables/table-card";

export function VisualInspection({ workOrder }: { workOrder: WorkOrder }) {
  const [assessmentId, setAssessmentId] = useState<string | null>(null);
  const { position, activeComponent } = useInspectionContext();

  return (
    <ScrollArea className="h-[65vh] space-y-2">
      <VisualAssessmentTable
        selectedAssessmentId={assessmentId}
        onAssessmentSelectedId={(a) => setAssessmentId(a)}
      />
      <div className="p-2 py-4">
        {position.visualAssessments
          .filter((a) => a.componentId === activeComponent?.componentId)
          .map((assessment) => {
            if (assessmentId !== assessment.id) return null;
            return (
              <VisualAssessmentForm
                key={assessment.id}
                workOrder={workOrder}
                assessment={assessment}
              />
            );
          })}
      </div>
    </ScrollArea>
  );
}

export function AddVisualAssessmentButton({
  workOrder,
}: {
  workOrder: WorkOrder;
}) {
  const {
    position,
    onPositionChange: [putPosition, { error, isLoading, reset }],
  } = useInspectionContext();

  const { data: visualAssessmentCatalog } = useGetVisualAssessmentCatalog(
    position.workOrderId,
    workOrder.companyId,
  );

  const { toast } = useToast();
  useEffect(() => {
    if (error) {
      toast({
        ...parseRTKQueryError(error),
        variant: "destructive",
      });
      reset();
    }
  }, [error, reset, toast]);
  const { activeComponent } = useInspectionContext();

  const { resetNotReadyErrorField } = useErrDocumentNotReady();
  const errorKey = `position.${position.id}.visualAssessments`;

  const doAdd = () => {
    if (isLoading) return;
    putPosition(
      addVisualAssessment(
        position,
        activeComponent!.componentId!,
        activeComponent!.id,
        v4(),
      ),
    );
    resetNotReadyErrorField(errorKey);
  };

  if (!visualAssessmentCatalog) {
    return null;
  }

  const doAddFromCatalog = (item: Item) => {
    if (isLoading || !position.ahuId) return;

    const assessment = fromCatalogItem(
      v4(),
      position.ahuId,
      activeComponent!.componentId!,
      item,
    );
    const woWithPosition = addVisualAssessment(
      position,
      activeComponent!.componentId!,
      activeComponent!.id,
      assessment.id,
    );
    const woWithAssessment = updateVisualAssessment(woWithPosition, assessment);
    putPosition(woWithAssessment);
    resetNotReadyErrorField(errorKey);
  };

  const disabled = workOrder.immutable || activeComponent === null || isLoading;

  const items = itemsForComponentKind(
    visualAssessmentCatalog,
    activeComponent?.componentKindId ?? "",
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild disabled={disabled}>
        <Button>
          <PlusCircle />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel>{t("Beurteilung hinzufügen")}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={doAdd}>
          <Plus className="mr-2 h-4 w-4" />
          <span>{t("Blanko")}</span>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuLabel>{t("Aus Vorlage:")}</DropdownMenuLabel>
        {items.map((item) => (
          <DropdownMenuItem
            onClick={() => doAddFromCatalog(item)}
            key={item.id}
          >
            <Plus className="mr-2 h-4 w-4" />
            <span className="flex items-center space-x-2">
              <ResultDot result={item.result} showLabel={false} />
              <span>{`${item.deficiency}`}</span>
            </span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function VisualAssessmentTable({
  selectedAssessmentId,
  onAssessmentSelectedId,
}: {
  selectedAssessmentId: string | null;
  onAssessmentSelectedId: (a: string | null) => void;
}) {
  const { position, activeComponent } = useInspectionContext();

  const toggle = (assessment: VisualAssessment) => {
    if (assessment.id === selectedAssessmentId) {
      onAssessmentSelectedId(null);
    } else {
      onAssessmentSelectedId(assessment.id);
    }
  };

  const { notReadyError } = useErrDocumentNotReady();
  const errorPrefix = `position.${position.id}.visualAssessments.`;

  return (
    <TableCard className="shadow-none">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="h-8">{t("Bewertung")}</TableHead>
            <TableHead className="h-8">{t("Mangel")}</TableHead>
            <TableHead className="h-8">{t("")}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {position.visualAssessments
            ?.filter((a) => a.componentId === activeComponent?.componentId)
            .map((assessment) => (
              <TableRow
                key={assessment.id}
                className={cn(
                  "cursor-pointer",
                  assessment.id === selectedAssessmentId ? "bg-accent" : "",
                )}
              >
                <TableCell className="py-1" onClick={() => toggle(assessment)}>
                  <span className="flex items-center space-x-2">
                    {hasFieldError(
                      notReadyError,
                      `${errorPrefix}${assessment.id}`,
                    ) && <AlertCircle className="h-6 w-6 text-red-500" />}
                    <ResultDot result={assessment.result} />
                  </span>
                </TableCell>
                <TableCell
                  className="w-full py-1"
                  onClick={() => toggle(assessment)}
                >
                  {assessment.deficiency}
                </TableCell>
                <TableCell className="py-1 text-right">
                  <DeleteAssessmentButton
                    assessmentId={assessment.id}
                    onDeleted={() => onAssessmentSelectedId(null)}
                  />
                </TableCell>
              </TableRow>
            ))}
        </TableBody>
      </Table>
    </TableCard>
  );
}

function DeleteAssessmentButton({
  assessmentId,
  onDeleted,
}: {
  assessmentId: string;
  onDeleted: () => void;
}) {
  const {
    position,
    onPositionChange: [putPosition, { error, isLoading, reset }],
  } = useInspectionContext();

  const { toast } = useToast();
  useEffect(() => {
    if (error) {
      toast({
        ...parseRTKQueryError(error),
        variant: "destructive",
      });
      reset();
    }
  }, [error, reset, toast]);

  const doDelete = () => {
    if (isLoading) return;

    onDeleted();
    putPosition(deleteVisualAssessment(position, assessmentId));
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button size="sm" variant="destructive" className="h-8 w-8 p-0">
          <Trash2 className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("Beurteilung löschen?")}</DialogTitle>
          <DialogDescription>
            {t(
              "Die Beurteilung wird unwiderruflich gelöscht. Diese Aktion kann nicht rückgängig gemacht werden.",
            )}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">{t("Abbrechen")}</Button>
          </DialogClose>
          <Button variant="destructive" onClick={doDelete}>
            {t("Löschen")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function VisualAssessmentForm({
  workOrder,
  assessment: propAssessment,
}: {
  workOrder: WorkOrder;
  assessment: VisualAssessment;
}) {
  const [assessment, setAssessment] = useState(propAssessment);
  const {
    position,
    onPositionChange: [putPosition],
  } = useInspectionContext();

  const { notReadyError, resetNotReadyErrorField } = useErrDocumentNotReady();
  const errorPrefix = `position.${position.id}.visualAssessments.${assessment.id}.`;

  const onChange = (a: VisualAssessment) => {
    setAssessment(a);
    putPosition(updateVisualAssessment(position, a));
  };

  return (
    <div className="h-full">
      <H4>{t("Beurteilung")}</H4>
      <Separator className="mt-1 mb-4" />
      <div className="grid gap-4">
        <div className="grid content-start gap-1.5">
          <Label>{t("Bewertung")}</Label>
          <ResultSelect
            result={assessment.result}
            onResultChange={(result) => {
              onChange({ ...assessment, result });
              resetNotReadyErrorField(`${errorPrefix}result`);
            }}
            disabled={workOrder.immutable}
          />
          <InputValidationErrors
            error={notReadyError}
            field={`${errorPrefix}result`}
          />
        </div>
        <div className="grid content-start gap-1.5">
          <Label>{t("Mangel")}</Label>
          <Textarea
            value={assessment.deficiency}
            onChange={(e) => {
              onChange({ ...assessment, deficiency: e.target.value });
              resetNotReadyErrorField(`${errorPrefix}deficiency`);
            }}
            className={cn(
              hasFieldError(notReadyError, `${errorPrefix}deficiency`) &&
                "border-red-500 shadow-md",
            )}
            disabled={workOrder.immutable}
          />
          <InputValidationErrors
            error={notReadyError}
            field={`${errorPrefix}deficiency`}
          />
        </div>
        <div className="grid content-start gap-1.5">
          <Label>{t("Handlungsempfehlung")}</Label>
          <Textarea
            value={assessment.recommendation}
            onChange={(e) => {
              onChange({ ...assessment, recommendation: e.target.value });
              resetNotReadyErrorField(`${errorPrefix}recommendation`);
            }}
            className={cn(
              hasFieldError(notReadyError, `${errorPrefix}recommendation`) &&
                "border-red-500 shadow-md",
            )}
            disabled={workOrder.immutable}
          />
          <InputValidationErrors
            error={notReadyError}
            field={`${errorPrefix}recommendation`}
          />
        </div>
        <div className="grid content-start gap-1.5">
          <Label>{t("Bemerkung (intern)")}</Label>
          <Textarea
            value={assessment.comment}
            onChange={(e) => {
              onChange({ ...assessment, comment: e.target.value });
              resetNotReadyErrorField(`${errorPrefix}comment`);
            }}
            className={cn(
              hasFieldError(notReadyError, `${errorPrefix}comment`) &&
                "border-red-500 shadow-md",
            )}
            disabled={workOrder.immutable}
          />
          <InputValidationErrors
            error={notReadyError}
            field={`${errorPrefix}comment`}
          />
        </div>
      </div>
    </div>
  );
}
